<template>
  <div class="stock-adjust">
    <div class="page-header">
      <h2>库存调整</h2>
    </div>
    
    <a-form
      :model="form"
      @finish="handleSubmit"
      layout="vertical"
    >
      <a-card title="调整信息" class="form-card">
        <a-row :gutter="16">
          <a-col :span="12">
            <a-form-item label="调整原因" name="reason">
              <a-input
                v-model:value="form.reason"
                placeholder="请输入调整原因"
                maxlength="100"
              />
            </a-form-item>
          </a-col>
        </a-row>
      </a-card>
      
      <a-card title="调整商品" class="form-card">
        <div class="product-section">
          <a-form-item label="选择商品" required>
            <a-select
              v-model:value="selectedProductId"
              placeholder="请输入商品名称进行搜索"
              show-search
              :filter-option="false"
              :loading="productsLoading"
              @search="handleProductSearch"
              @change="onProductChange"
              @clear="handleProductClear"
              style="width: 400px"
              allow-clear
            >
              <a-select-option
                v-for="product in products"
                :key="product.id"
                :value="product.id"
              >
                {{ product.name }}
              </a-select-option>
            </a-select>
          </a-form-item>
          
          <a-form-item label="选择仓库" required>
            <a-select
              v-model:value="selectedWarehouseId"
              placeholder="请选择仓库"
              @change="onWarehouseChange"
              style="width: 200px"
            >
              <a-select-option
                v-for="warehouse in warehouses"
                :key="warehouse.id"
                :value="warehouse.id"
              >
                {{ warehouse.name }}
              </a-select-option>
            </a-select>
          </a-form-item>
        </div>
        
        <div v-if="!selectedProductId || !selectedWarehouseId" class="empty-sku">
          <a-empty description="请先选择商品和仓库" />
        </div>
        
        <div v-else-if="availableSkus.length === 0" class="empty-sku">
          <a-empty description="该商品暂无SKU信息" />
        </div>
        
        <div v-else class="sku-section">
          <div style="border: 1px solid #d9d9d9; border-radius: 6px; padding: 16px;">
            <!-- 表头 -->
            <div style="display: grid; grid-template-columns: 1fr 1fr 1fr 1fr 1fr; gap: 12px; margin-bottom: 12px; font-weight: 500; color: #666;">
              <div style="text-align: center;">颜色</div>
              <div style="text-align: center;">尺码</div>
              <div style="text-align: center;">现库存</div>
              <div style="text-align: center;">调整后数量</div>
              <div style="text-align: center;">变化量</div>
            </div>
            
            <!-- SKU选项列表 -->
            <div style="max-height: 300px; overflow-y: auto;">
              <div
                v-for="sku in availableSkus"
                :key="sku.key"
                :class="['sku-row']"
                style="display: grid; grid-template-columns: 1fr 1fr 1fr 1fr 1fr; gap: 12px; padding: 8px; border-radius: 4px; margin-bottom: 4px; align-items: center;"
              >
                <div style="text-align: center; font-weight: 500;">{{ sku.color }}</div>
                <div style="text-align: center; font-weight: 500;">{{ sku.size }}</div>
                <div style="text-align: center;">
                  <a-tag :color="sku.stockQuantity > 10 ? 'green' : sku.stockQuantity > 0 ? 'orange' : 'red'" style="margin: 0;">
                    {{ sku.stockQuantity }}
                  </a-tag>
                </div>
                <div style="text-align: center;">
                  <a-input-number
                    v-model:value="sku.adjustedQuantity"
                    :min="0"
                    size="small"
                    style="width: 80px;"
                    @change="updateSkuChangeAmount(sku)"
                  />
                </div>
                <div style="text-align: center;">
                  <a-tag 
                    v-if="sku.changeAmount !== undefined && sku.changeAmount !== 0"
                    :color="getChangeColor(sku.changeAmount)"
                    style="margin: 0;"
                  >
                    {{ sku.changeAmount >= 0 ? '+' : '' }}{{ sku.changeAmount }}
                  </a-tag>
                  <span v-else style="color: #999;">-</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </a-card>
      
      <div class="form-actions">
        <a-button type="primary" html-type="submit" size="large" :loading="loading">
          确认调整
        </a-button>
        <a-button size="large" @click="resetForm" style="margin-left: 16px">
          重置
        </a-button>
      </div>
    </a-form>
  </div>
</template>

<script>
import { reactive, ref, onMounted } from 'vue'
import { message } from 'ant-design-vue'
import { stockApi, productApi, warehouseApi } from '@/api'

export default {
  name: 'StockAdjust',
  components: {},
  setup() {
    const loading = ref(false)
    const products = ref([])
    const productsLoading = ref(false)
    const warehouses = ref([])
    const productDetails = ref({})
    const selectedProductId = ref(undefined)
    const selectedWarehouseId = ref(undefined)
    const availableSkus = ref([])
    const currentProduct = ref(null)
    let searchTimeout = null
    
    const form = reactive({
      reason: '盘点调整',
      items: []
    })
    
    const loadProducts = async (searchName = '') => {
      productsLoading.value = true
      try {
        const result = await productApi.querySimpleInfo({
          name: searchName,
          status: 1
        })
        products.value = result.data || []
      } catch (error) {
        console.error('加载商品失败:', error)
        products.value = []
      } finally {
        productsLoading.value = false
      }
    }
    
    const handleProductSearch = (value) => {
      if (searchTimeout) {
        clearTimeout(searchTimeout)
      }
      
      searchTimeout = setTimeout(() => {
        loadProducts(value)
      }, 300)
    }
    
    const handleProductClear = () => {
      products.value = []
      selectedProductId.value = undefined
      availableSkus.value = []
      currentProduct.value = null
    }
    
    const loadWarehouses = async () => {
      try {
        const result = await warehouseApi.list()
        warehouses.value = result.data || []
      } catch (error) {
        console.error('加载仓库失败:', error)
      }
    }
    
    const loadProductDetail = async (productId) => {
      try {
        const result = await productApi.detail(productId)
        currentProduct.value = result.data
        productDetails.value[productId] = result.data
        return result.data
      } catch (error) {
        console.error('加载商品详情失败:', error)
        return null
      }
    }
    
    const loadAvailableSkus = (warehouseId) => {
      if (!currentProduct.value || !warehouseId) {
        availableSkus.value = []
        return
      }
      
      // 从商品的stockItems中筛选出当前仓库的SKU
      const skus = currentProduct.value.stockItems?.filter(
        item => item.warehouseId === warehouseId
      ) || []
      
      availableSkus.value = skus.map(item => ({
        key: `${item.warehouseId}-${item.color || 'default'}-${item.size || 'default'}`,
        color: item.color || '默认颜色',
        size: item.size || '默认尺码',
        stockQuantity: parseInt(item.stockQuantity) || 0,
        warehouseId: item.warehouseId,
        adjustedQuantity: parseInt(item.stockQuantity) || 0,
        changeAmount: 0
      }))
    }
    
    const getChangeColor = (changeAmount) => {
      if (changeAmount === undefined || changeAmount === 0) return 'default'
      if (changeAmount > 0) return 'green'
      if (changeAmount < 0) return 'red'
      return 'default'
    }
    
    const onProductChange = async (productId) => {
      selectedWarehouseId.value = undefined
      availableSkus.value = []
      
      if (productId) {
        await loadProductDetail(productId)
        // 如果只有一个仓库，自动选中
        if (warehouses.value.length === 1) {
          selectedWarehouseId.value = warehouses.value[0].id
          loadAvailableSkus(warehouses.value[0].id)
        }
      }
    }
    
    const onWarehouseChange = (warehouseId) => {
      loadAvailableSkus(warehouseId)
    }
    
    const updateSkuChangeAmount = (sku) => {
      if (sku.adjustedQuantity === undefined || sku.adjustedQuantity === null) {
        sku.changeAmount = 0
        return
      }
      
      const currentStock = parseInt(sku.stockQuantity) || 0
      const adjustedQuantity = parseInt(sku.adjustedQuantity) || 0
      
      sku.changeAmount = adjustedQuantity - currentStock
    }
    
    
    const resetForm = () => {
      form.reason = '盘点调整'
      selectedProductId.value = undefined
      selectedWarehouseId.value = undefined
      availableSkus.value = []
      currentProduct.value = null
    }
    
    const handleSubmit = async () => {
      if (!selectedProductId.value || !selectedWarehouseId.value) {
        message.error('请先选择商品和仓库')
        return
      }
      
      // 发送所有SKU数据给后端，包括没有变化的
      const allSkus = availableSkus.value
      
      if (allSkus.length === 0) {
        message.warning('没有可调整的SKU')
        return
      }
      
      loading.value = true
      try {
        // 构建请求数据 - 发送全部SKU数据
        const requestData = {
          reason: form.reason,
          productId: selectedProductId.value,
          warehouseId: selectedWarehouseId.value,
          items: allSkus.map(sku => ({
            productId: selectedProductId.value,
            warehouseId: selectedWarehouseId.value,
            color: sku.color,
            size: sku.size,
            newQuantity: sku.adjustedQuantity,
            changeAmount: sku.changeAmount
          }))
        }
        
        await stockApi.adjust(requestData)
        
        const changedSkus = allSkus.filter(sku => sku.changeAmount !== 0)
        const totalChanges = changedSkus.reduce((sum, sku) => sum + Math.abs(sku.changeAmount), 0)
        message.success(`库存调整成功 - 共提交${allSkus.length}个SKU，其中${changedSkus.length}个有变化，总变化量：${totalChanges}`)
        
        resetForm()
      } catch (error) {
        console.error('库存调整失败:', error)
        message.error(`库存调整失败: ${error.message || '未知错误'}`)
      } finally {
        loading.value = false
      }
    }
    
    onMounted(() => {
      loadWarehouses()
      loadProducts() // 初始加载一些商品
    })
    
    return {
      form,
      loading,
      products,
      productsLoading,
      warehouses,
      selectedProductId,
      selectedWarehouseId,
      availableSkus,
      loadProducts,
      handleProductSearch,
      handleProductClear,
      getChangeColor,
      onProductChange,
      onWarehouseChange,
      updateSkuChangeAmount,
      resetForm,
      handleSubmit
    }
  }
}
</script>

<style scoped>
.stock-adjust {
  padding: 0;
}

.page-header {
  margin-bottom: 24px;
}

.page-header h2 {
  margin: 0;
}

.form-card {
  margin-bottom: 16px;
}

.product-section {
  margin-bottom: 24px;
}

.product-section .ant-form-item {
  display: inline-block;
  margin-right: 24px;
  margin-bottom: 16px;
}

.empty-sku {
  padding: 40px 0;
  text-align: center;
}

.sku-section {
  margin-top: 16px;
}

.sku-row {
  transition: all 0.2s ease;
  border: 1px solid transparent;
}

.sku-row:hover {
  background-color: #f5f5f5;
  border-color: #d9d9d9;
}

.form-actions {
  text-align: center;
  padding: 24px 0;
  border-top: 1px solid #f0f0f0;
  margin-top: 32px;
}
</style>